import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiCheck } from 'react-icons/bi';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import ProBadge from '@/components/ui/ProBadge';
import InfoMessageAlert from '@/features/bot/InfoMessageAlert';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { selectCurrentDataState, updateData } from '../../create.slice';

const AutoUpdate = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setValue, formState, trigger, control, watch } = useFormContext();

  const currentData = useAppSelector(selectCurrentDataState);
  const plugAndPlayPermissions = useAppSelector(state => (state.dashboardState as any).Client.botConfigs).find(
    (item: any) => item.botName === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY
  )?.config;

  const autoUpdateRules: Array<{
    title: string;
    id: string;
    description: string;
  }> = [
    {
      id: 'never',
      title: t('Wizard/PlugAndPlayInfos/key5'),
      description: t('Wizard/PlugAndPlayInfos/key6'),
    },
    {
      id: 'weekly',
      title: t('Wizard/PlugAndPlayInfos/key7'),
      description: t('Wizard/PlugAndPlayInfos/key8'),
    },
    {
      id: 'daily',
      title: t('Wizard/PlugAndPlayInfos/key9'),
      description: t('Wizard/PlugAndPlayInfos/key10'),
    },
  ];

  const handleUpdate = () => {
    dispatch(
      updateData({
        plug_and_play: {
          ...currentData?.plug_and_play,
          auto_update: watch('plug_and_play.auto_update'),
        },
      })
    );
  };

  // TODO => validation logic via permissions
  const hasPermission = (type: string) => {
    return type === 'never' ? true : plugAndPlayPermissions?.allowed_schedule?.includes(type);
  };

  useEffect(() => {
    if (currentData?.plug_and_play?.auto_update) {
      setValue('plug_and_play', {
        ...(currentData.plug_and_play || {}),
        auto_update: currentData?.plug_and_play?.auto_update,
      });
    }
    return () => {
      setValue('plug_and_play', {
        ...(currentData.plug_and_play || {}),
        auto_update: '',
      });
    };
  }, [currentData.plug_and_play]);

  return (
    <div className="plug-and-play tw-py-4 md:tw-px-4">
      <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-inline-flex">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            4. {t('Wizard/PlugAndPlayInfos/key3')}
          </div>
          <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('Wizard/PlugAndPlayInfos/key4')}
          </div>
        </div>

        <InfoMessageAlert texts={[t('Wizard/PlugAndPlayAlert/key1')]} />

        <div className="tw-self-stretch tw-min-h-[104px] tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex">
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-5 tw-flex  md:tw-flex-row tw-flex-col">
            {autoUpdateRules.map((rule, index) => (
              <Controller
                key={index}
                control={control}
                name="plug_and_play.address"
                render={() => (
                  <div
                    className={`tw-relative tw-h-[132px] md:tw-max-w-[251.2px] tw-w-full tw-px-4 tw-pt-4 tw-pb-6 tw-bg-white tw-rounded-2xl tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex ${
                      currentData?.plug_and_play?.auto_update === rule.id ? ' tw-border-blue-500' : ' tw-border-transparent'
                    } ${hasPermission(rule.id) ? 'tw-cursor-pointer' : 'tw-cursor-not-allowed'}`}
                    role="button"
                    id={`btn-plug-play-${rule.id}`}
                    onClick={() => {
                      if (hasPermission(rule.id)) {
                        setValue('plug_and_play', {
                          ...(currentData.plug_and_play || {}),
                          auto_update: rule.id,
                        });

                        trigger('plug_and_play.auto_update');
                        handleUpdate();
                      }
                    }}
                    onKeyDown={() => {
                      if (hasPermission(rule.id)) {
                        setValue('plug_and_play', {
                          ...(currentData.plug_and_play || {}),
                          auto_update: rule.id,
                        });
                        trigger('plug_and_play.auto_update');
                        handleUpdate();
                      }
                    }}
                    tabIndex={0}
                  >
                    <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                      {currentData?.plug_and_play?.auto_update === rule.id ? (
                        <div className="tw-w-4 tw-h-4 tw-bg-primary tw-rounded tw-border tw-border-blue-600 tw-justify-center tw-items-center tw-flex">
                          <BiCheck className="tw-text-white" size={16} />
                        </div>
                      ) : (
                        <div
                          className={`tw-w-4 tw-h-4 tw-relative ${
                            !hasPermission(rule.id) ? 'tw-bg-gray-200' : 'tw-bg-white '
                          } tw-rounded tw-border tw-border-solid tw-border-gray-300`}
                        />
                      )}

                      {!hasPermission(rule.id) && <ProBadge variant="large" />}
                    </div>
                    <div className="tw-self-stretch tw-h-16 tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                      <div className="tw-self-stretch tw-text-gray-800 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">{rule.title}</div>
                      <div className="tw-self-stretch tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{rule.description}</div>
                    </div>
                  </div>
                )}
              />
            ))}
          </div>
          {!!(formState.errors.plug_and_play as any)?.auto_update && (
            <span className="tw-text-sm tw-text-red-500 tw-block">{(formState.errors?.plug_and_play as any)?.auto_update?.message as string}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(AutoUpdate);
